// Run aggregate root — the heart of Hermes. An immutable, framework-free
// model of a single agent execution and (for roots) its one level of fan-out.
// It defers ALL transition legality to runState.js (the canonical table) and
// adds the one invariant the table can't express on its own: a parent may not
// enter `merging` while any child run is still non-terminal (children_pending).
//
// Depth is 1 by design: only a root run may fan out; a child never does.
// The aggregate is time-agnostic — callers pass every timestamp (`at`); the
// domain never calls Date.now()/new Date(). Every mutator returns a NEW frozen
// Run, leaving the receiver untouched.

import { HermesDomainError } from './errors.js';
import { requireNonEmpty } from './valueObjects.js';
import { STATES, TERMINAL, isTerminal, assertTransition } from './runState.js';

const KNOWN_STATES = Object.values(STATES);

export function createRun(props = {}) {
  const {
    id,
    agentId,
    parentRunId = null,
    userId,
    task,
    state = STATES.QUEUED,
    result = null,
    error = null,
    // root — infra passthrough: the worktree/cwd a fan-out child executes in,
    // consumed by the execution adapter later. Stored, not interpreted here.
    root = null,
    childIndex = null,
    tokens = 0,
    cost = 0,
    createdAt = null,
    startedAt = null,
    finishedAt = null,
  } = props;

  requireNonEmpty(id, 'id', 'invalid_run');
  requireNonEmpty(agentId, 'agentId', 'invalid_run');
  requireNonEmpty(userId, 'userId', 'invalid_run');
  requireNonEmpty(task, 'task', 'invalid_run');

  if (!KNOWN_STATES.includes(state)) {
    throw new HermesDomainError('invalid_run', `unknown state: ${state}`);
  }

  const fields = {
    id,
    agentId,
    parentRunId: parentRunId ?? null,
    userId,
    task,
    state,
    result,
    error,
    root,
    childIndex,
    tokens: tokens ?? 0,
    cost: cost ?? 0,
    createdAt,
    startedAt,
    finishedAt,
  };

  // Build the next Run after a validated transition. `to` is the target state;
  // `patch` carries the fields that change with this transition. We re-run
  // assertTransition here so EVERY mutator goes through the canonical table.
  const transitionTo = (to, patch) => {
    assertTransition(fields.state, to);
    return createRun({ ...fields, ...patch, state: to });
  };

  const run = {
    ...fields,

    isRoot() {
      return this.parentRunId == null;
    },

    isChild() {
      return !this.isRoot();
    },

    isTerminal() {
      return TERMINAL.includes(this.state);
    },

    isRunning() {
      return ['running', 'fanning_out', 'merging'].includes(this.state);
    },

    canCancel() {
      return !this.isTerminal();
    },

    // Depth-1 fan-out: only a root currently running may fan out, and only
    // within the agent's cap. A child always returns false.
    canFanOut(subtaskCount, agentFanoutCap) {
      return (
        this.isRoot() &&
        this.state === STATES.RUNNING &&
        subtaskCount >= 1 &&
        subtaskCount <= agentFanoutCap
      );
    },

    start({ at } = {}) {
      return transitionTo(STATES.RUNNING, { startedAt: at });
    },

    beginFanOut() {
      return transitionTo(STATES.FANNING_OUT, {});
    },

    // THE fan-out merge invariant: the parent cannot enter `merging` while any
    // child is still non-terminal. Require a non-empty array of child states,
    // all terminal — otherwise children_pending (checked BEFORE the state edge
    // so the error is specific to the invariant, not the table).
    beginMerge({ childStates } = {}) {
      if (!Array.isArray(childStates) || childStates.length === 0) {
        throw new HermesDomainError(
          'children_pending',
          'beginMerge requires a non-empty array of child states',
        );
      }
      const pending = childStates.filter((s) => !isTerminal(s));
      if (pending.length > 0) {
        throw new HermesDomainError(
          'children_pending',
          `cannot merge while children are non-terminal: ${pending.join(', ')}`,
        );
      }
      return transitionTo(STATES.MERGING, {});
    },

    /**
     * One-shot terminal transition to `done`. OVERWRITES `result` (it is not
     * accumulator-only); tokens/cost, when provided, ARE accumulated onto the
     * prior totals.
     */
    complete({ result: res = null, at, tokens: tk, cost: ct } = {}) {
      const patch = { result: res, finishedAt: at };
      if (tk != null) patch.tokens = this.tokens + tk;
      if (ct != null) patch.cost = this.cost + ct;
      return transitionTo(STATES.DONE, patch);
    },

    fail({ error: err = null, at } = {}) {
      return transitionTo(STATES.FAILED, { error: err, finishedAt: at });
    },

    cancel({ at } = {}) {
      return transitionTo(STATES.CANCELLED, { finishedAt: at });
    },

    // toRecord — a plain, function-free snapshot of ONLY the data fields,
    // suitable for the repo layer to map onto D1 columns.
    toRecord() {
      return {
        id: this.id,
        agentId: this.agentId,
        parentRunId: this.parentRunId,
        userId: this.userId,
        task: this.task,
        state: this.state,
        result: this.result,
        error: this.error,
        root: this.root,
        childIndex: this.childIndex,
        tokens: this.tokens,
        cost: this.cost,
        createdAt: this.createdAt,
        startedAt: this.startedAt,
        finishedAt: this.finishedAt,
      };
    },
  };

  return Object.freeze(run);
}
